import { Fragment, useEffect, useMemo, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, ChevronRight, History, Pause, Play, SlidersHorizontal, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { RowActions } from '@/components/ui/row-actions';
import { PlatformIcon } from '@/components/PlatformIcon';
import { useUrlState } from '@/hooks/useUrlState';
import { ApiError, type RepeatRunsSummary, type RepeatSchedule } from '../api/types';
import { useAllSchedules, useDeleteSchedule, useUpdateSchedule } from '../hooks/schedules';
import { relativeTime } from '../search/controls';
import { useDiscoveryPaths } from '../workspace/paths';
import { Field, FolderDot, Note, Segmented } from '../workspace/ui';
import { HealthPill } from '../repeat-runs/HealthPill';
import { RunSparkline } from '../repeat-runs/RunSparkline';
import { ScheduleRunsSheet } from '../repeat-runs/ScheduleRunsSheet';
import { OutsideTargetBadge, RunsWithLine } from '../repeat-runs/ScheduleSettings';
import { ScheduleEditDialog } from '../repeat-runs/ScheduleEditDialog';
import { ScheduleCreators } from '../repeat-runs/ScheduleCreators';
import { HEALTH_FILTERS, cadenceLabel, count, formatDay, pausedForDry } from '../repeat-runs/model';
import { useAutoPauseAfterDryRuns, useOutcomes } from '../suggestions/hooks';
import { formatRate, type Outcome } from '../suggestions/model';

const URL_DEFAULTS = { folder: '', health: '', view: 'active', schedule: '', run: '', open: '' };

/** Tracks a CSS media query — Tailwind's lg / xl here. */
function useMinWidth(px: number): boolean {
  const query = `(min-width: ${px}px)`;
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const on = () => setMatches(mql.matches);
    on();
    mql.addEventListener('change', on);
    return () => mql.removeEventListener('change', on);
  }, [query]);
  return matches;
}

type View = 'active' | 'paused' | 'all';
const asView = (v: string): View => (v === 'paused' || v === 'all' ? v : 'active');
/** Paused is its own view, so the health filter doesn't offer it twice. */
const HEALTH_OPTIONS = HEALTH_FILTERS.filter((h) => h.value !== 'paused');

/**
 * Every scheduled search ("repeat run") the team has, across folders: is each
 * one still finding people, what did its recent runs add, and — one click in —
 * exactly which creators a given run added.
 *
 * Schedules are created from a folder's search form ("Repeat this search");
 * this page is where you watch them and switch off the ones that stopped
 * earning.
 */
export function RepeatRunsScreen(): JSX.Element {
  const paths = useDiscoveryPaths();
  const [url, setUrl] = useUrlState(URL_DEFAULTS);
  const { data, isLoading, error } = useAllSchedules();
  const all = useMemo(() => data?.schedules ?? [], [data]);
  const outcomes = useOutcomes();
  const autoPauseAfter = useAutoPauseAfterDryRuns();
  // The column only exists once some schedule has a rate to show in it.
  const showYield = all.some((s) => s.postsPer100 != null || outcomes.bySchedule.get(s.publicId)?.postsPer100 != null);
  const autoPaused = all.filter(pausedForDry).length;

  const folders = useMemo(() => {
    const seen = new Map<string, string>();
    for (const s of all) if (s.folder) seen.set(s.folder.publicId, s.folder.name);
    return [...seen].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [all]);

  const view = asView(url.view);
  // An expanded row spans the columns actually on screen. Spanning a hidden
  // (display: none) column makes the browser add a phantom one that eats
  // Search's width.
  const lg = useMinWidth(1024);
  const xl = useMinWidth(1280);
  const columns = 5 + (lg ? 1 : 0) + (xl ? 1 + (showYield ? 1 : 0) : 0);
  const pausedCount = all.filter((s) => !s.enabled).length;
  const rows = useMemo(
    () =>
      all.filter(
        (s) =>
          (!url.folder || s.folder?.publicId === url.folder) &&
          (!url.health || s.health === url.health) &&
          (view === 'all' || (view === 'paused' ? !s.enabled : s.enabled)),
      ),
    [all, url.folder, url.health, view],
  );

  const open = url.schedule ? all.find((s) => s.publicId === url.schedule) ?? null : null;
  // The earliest "history starts" among schedules whose early runs weren't recorded.
  const partial = all.filter((s) => !s.historyComplete && s.historySince);
  const filtered = !!url.folder || !!url.health;
  // An older Discovery service has no cross-folder endpoint at all.
  const unsupported = error instanceof ApiError && error.status === 404;

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-[26px] font-bold tracking-[-0.03em]">Repeat runs</h1>
        <p className="mt-1 max-w-[75ch] text-sm text-muted-foreground">
          Searches that re-run themselves into a folder. Each run only adds creators the team hasn't seen, so the
          number to watch is how many a run added.
        </p>
      </header>

      {unsupported && (
        <Note tone="warning" role="status">
          This page needs a Discovery service update that hasn't finished deploying. Your scheduled searches are still
          running, and each folder's Schedules tab still lists them.
        </Note>
      )}
      {error && !unsupported && (
        <Note tone="error" role="alert">
          Couldn't load repeat runs: {(error as Error).message}
        </Note>
      )}

      {data && all.length > 0 && <SummaryStrip summary={data.summary} />}

      {data && all.length > 0 && (
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Folder">
            <Combobox
              className="w-full md:w-56"
              value={url.folder}
              onChange={(v) => setUrl({ folder: v })}
              allowAll
              allLabel="All folders"
              aria-label="Folder"
              options={folders}
            />
          </Field>
          <Field label="Health">
            <Combobox
              className="w-full md:w-44"
              value={url.health}
              onChange={(v) => setUrl({ health: v })}
              allowAll
              allLabel="Any health"
              aria-label="Health"
              options={HEALTH_OPTIONS}
            />
          </Field>
          <Field label="Show">
            <Segmented
              label="Show"
              value={view}
              onChange={(v) => setUrl({ view: v, open: '' })}
              options={[
                { value: 'active', label: `Active (${(all.length - pausedCount).toLocaleString()})` },
                { value: 'paused', label: `Paused (${pausedCount.toLocaleString()})` },
                { value: 'all', label: 'All' },
              ]}
            />
          </Field>
          {filtered && (
            <Button type="button" variant="ghost" size="sm" onClick={() => setUrl({ folder: '', health: '' })}>
              Clear filters
            </Button>
          )}
        </div>
      )}

      {data && all.length > 0 && (autoPauseAfter != null || autoPaused > 0) && (
        <p className="text-sm text-muted-foreground" data-auto-pause>
          {autoPauseAfter != null
            ? `A schedule pauses itself after ${autoPauseAfter} run${autoPauseAfter === 1 ? '' : 's'} in a row that add nobody.`
            : 'A schedule pauses itself when its runs keep adding nobody.'}
          {autoPaused > 0 && ` ${autoPaused} ${autoPaused === 1 ? 'is' : 'are'} paused for that now.`}{' '}
          <Link to={paths.settings} className="underline underline-offset-2 hover:text-foreground">
            Change this in Settings
          </Link>
        </p>
      )}

      {isLoading && <p className="text-sm text-muted-foreground">Loading repeat runs…</p>}

      {data && all.length === 0 && (
        <div className="panel rounded-xl">
          <EmptyState
            icon={<CalendarClock className="h-7 w-7" />}
            title="No repeat runs yet"
            hint='To create one, open a folder, go to Find creators, tick "Repeat this search" and choose how often before you press Search. The search runs once now and then again on that cadence.'
          />
          <div className="flex justify-center pb-8">
            <Button asChild variant="outline" size="sm">
              <Link to={paths.home}>Go to folders</Link>
            </Button>
          </div>
        </div>
      )}

      {data && all.length > 0 && rows.length === 0 && (
        <EmptyState
          title={view === 'active' && !filtered ? 'No active schedules' : 'No schedules match these filters'}
          hint={
            view === 'active' && pausedCount > 0
              ? `${pausedCount.toLocaleString()} paused schedule${pausedCount === 1 ? ' is' : 's are'} hidden. Choose Paused or All to see ${pausedCount === 1 ? 'it' : 'them'}.`
              : 'Clear a filter to see more.'
          }
        />
      )}

      {rows.length > 0 && (
        <div className="table-frame">
          {/* Fixed layout: the narrow columns get set widths and Search takes the
              rest, so the row fits the page instead of scrolling sideways. */}
          <table className="w-full table-fixed text-sm" aria-label="Repeat runs">
            <thead>
              <tr className="text-left text-xs font-semibold text-muted-foreground/80">
                <th className="w-10 py-2 pl-1">
                  <span className="sr-only">Creators</span>
                </th>
                <th className="py-2 pr-4 font-semibold">Search</th>
                <th className="w-36 py-2 pr-4 font-semibold">Health</th>
                <th className="hidden w-28 py-2 pr-4 text-right font-semibold lg:table-cell">Last run</th>
                <th className="w-24 py-2 pr-4 text-right font-semibold">Total added</th>
                {showYield && (
                  <th
                    className="hidden w-28 py-2 pr-4 text-right font-semibold xl:table-cell"
                    title="Posts per 100 creators this schedule added"
                  >
                    Posts / 100 leads
                  </th>
                )}
                <th className="hidden w-44 py-2 pr-4 font-semibold xl:table-cell">Added per recent run</th>
                <th className="w-12 py-2 pr-1 text-right font-semibold">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <ScheduleRow
                  key={s.publicId}
                  schedule={s}
                  columns={columns}
                  expanded={url.open === s.publicId}
                  onToggle={() => setUrl({ open: url.open === s.publicId ? '' : s.publicId })}
                  outcome={showYield ? outcomes.bySchedule.get(s.publicId) ?? null : undefined}
                  onHistory={() => setUrl({ schedule: s.publicId, run: '' })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {partial.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Run-by-run history starts {formatDay(partial.map((s) => s.historySince!).sort()[0]!)} for{' '}
          {partial.length === 1 ? 'one older schedule' : `${partial.length} older schedules`}; their earlier runs
          were counted but not recorded individually.
        </p>
      )}

      <ScheduleRunsSheet
        publicId={url.schedule || null}
        fallback={open}
        runJobId={url.run || null}
        onRun={(jobId) => setUrl({ run: jobId ?? '' })}
        onClose={() => setUrl({ schedule: '', run: '' })}
      />
    </div>
  );
}

function SummaryStrip({ summary }: { summary: RepeatRunsSummary }): JSX.Element {
  const tiles: { label: string; value: string; hint: string; tone?: 'warn' | 'bad' }[] = [
    {
      label: 'Active schedules',
      value: summary.active.toLocaleString(),
      hint: summary.paused > 0 ? `${summary.paused.toLocaleString()} paused` : 'none paused',
    },
    { label: 'Runs, last 7 days', value: count(summary.runsLast7Days), hint: 'scheduled runs started' },
    { label: 'Added, last 7 days', value: count(summary.addedLast7Days), hint: 'new creators from repeat runs' },
    { label: 'Added, last 30 days', value: count(summary.addedLast30Days), hint: 'new creators from repeat runs' },
    {
      label: 'Dry schedules',
      value: summary.dry.toLocaleString(),
      hint: summary.failing > 0 ? `${summary.failing.toLocaleString()} failing` : 'active but adding nobody',
      tone: summary.failing > 0 ? 'bad' : summary.dry > 0 ? 'warn' : undefined,
    },
  ];
  return (
    <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5" aria-label="Repeat runs summary">
      {tiles.map((t) => (
        <div key={t.label} className="panel rounded-xl px-3 py-2.5" data-summary={t.label}>
          <dt className="truncate text-sm text-muted-foreground">{t.label}</dt>
          <dd
            className={cn(
              'mt-0.5 font-mono text-lg font-semibold tabular-nums',
              t.tone === 'warn' && 'text-pending-strong',
              t.tone === 'bad' && 'text-destructive-strong',
            )}
          >
            {t.value}
          </dd>
          <dd className="truncate text-xs text-muted-foreground">{t.hint}</dd>
        </div>
      ))}
    </dl>
  );
}

function ScheduleRow({
  schedule: s,
  columns,
  expanded,
  onToggle,
  outcome,
  onHistory,
}: {
  schedule: RepeatSchedule;
  /** How many columns the table has — the expanded panel spans them all. */
  columns: number;
  expanded: boolean;
  onToggle: () => void;
  /** Undefined = the page isn't showing the yield column at all. */
  outcome?: Outcome | null;
  onHistory: () => void;
}): JSX.Element {
  const paths = useDiscoveryPaths();
  const folderId = s.folder?.publicId ?? '';
  const update = useUpdateSchedule(folderId);
  const remove = useDeleteSchedule(folderId);
  const last = s.recentRuns[s.recentRuns.length - 1] ?? null;
  const archived = s.folder?.archived ?? false;
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const postsPer100 = s.postsPer100 ?? outcome?.postsPer100 ?? null;
  const nextLine = !s.enabled
    ? s.pausedByArchive
      ? 'Paused with its folder'
      : pausedForDry(s)
        ? 'Paused automatically'
        : 'Not scheduled'
    : new Date(s.nextRunAt).getTime() <= Date.now()
      ? 'Next run due now'
      : `Next ${relativeTime(s.nextRunAt)}`;

  const togglePause = () =>
    update.mutate(
      { publicId: s.publicId, patch: { enabled: !s.enabled } },
      {
        onError: (e) =>
          toast.error(`Couldn't ${s.enabled ? 'pause' : 'resume'} the schedule`, { description: (e as Error).message }),
      },
    );

  return (
    <Fragment>
      <tr
        className={cn(
          'border-t border-border align-middle transition-colors hover:bg-accent/40',
          !s.enabled && 'text-foreground/75',
          expanded && 'bg-accent/30',
        )}
        data-schedule={s.publicId}
      >
        <td className="py-2.5 pl-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Hide' : 'Show'} creators added by ${s.name}`}
            title={expanded ? 'Hide creators' : 'Show creators'}
          >
            <ChevronRight className={cn('h-4 w-4 transition-transform', expanded && 'rotate-90')} />
          </Button>
        </td>
        <td className="py-2.5 pr-4">
          <div className="flex min-w-0 items-center gap-2">
            <PlatformIcon platform={s.platform} size={14} className="shrink-0 text-muted-foreground" />
            <button
              type="button"
              onClick={onHistory}
              className="min-w-0 truncate rounded text-left font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              title={s.name}
            >
              {s.name}
            </button>
          </div>
          <p className="flex min-w-0 flex-wrap items-center gap-x-1.5 pl-[22px] text-xs text-muted-foreground">
            {s.folder ? (
              <Link
                to={paths.folder(s.folder.publicId, 'schedules')}
                className="inline-flex min-w-0 max-w-[16rem] items-center gap-1.5 text-foreground/80 hover:underline"
              >
                <FolderDot folder={s.folder} />
                <span className="truncate">{s.folder.name}</span>
              </Link>
            ) : (
              <span>No folder</span>
            )}
            {archived && <span>(archived)</span>}
            <span aria-hidden>·</span>
            <span className="whitespace-nowrap">{cadenceLabel(s.everyDays)}</span>
            <span aria-hidden>·</span>
            <span className="whitespace-nowrap">{nextLine}</span>
            {s.createdByName && (
              <>
                <span aria-hidden>·</span>
                <span className="whitespace-nowrap">By {s.createdByName}</span>
              </>
            )}
          </p>
          <RunsWithLine summary={s.paramsSummary} onShowAll={onHistory} />
          <OutsideTargetBadge count={s.outsideTargetTotal} className="ml-[22px] mt-1" />
        </td>
        <td className="py-2.5 pr-4">
          <HealthPill schedule={s} />
        </td>
        <td className="hidden whitespace-nowrap py-2.5 pr-4 text-right lg:table-cell">
          {s.runCount === 0 && !last ? (
            <span className="text-muted-foreground">—</span>
          ) : last?.status === 'failed' ? (
            <span className="font-semibold text-destructive-strong">Failed</span>
          ) : last?.status === 'running' ? (
            <span className="font-semibold">Running</span>
          ) : (
            <span className={cn('font-semibold', (s.lastAdded ?? 0) === 0 && 'font-normal text-muted-foreground')}>
              +{(s.lastAdded ?? 0).toLocaleString()} added
            </span>
          )}
          <p className="text-xs font-normal text-muted-foreground">{s.lastRunAt ? relativeTime(s.lastRunAt) : 'never run'}</p>
        </td>
        <td className="whitespace-nowrap py-2.5 pr-4 text-right">
          {s.totalAdded > 0 ? (
            <button
              type="button"
              onClick={onToggle}
              className="rounded font-semibold tabular-nums underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={`Show the ${s.totalAdded.toLocaleString()} creators ${s.name} added`}
            >
              {s.totalAdded.toLocaleString()}
            </button>
          ) : (
            <span className="tabular-nums">0</span>
          )}
          <p className="text-xs text-muted-foreground">
            {s.runCount.toLocaleString()} run{s.runCount === 1 ? '' : 's'}
          </p>
        </td>
        {outcome !== undefined && (
          <td className="hidden whitespace-nowrap py-2.5 pr-4 text-right tabular-nums xl:table-cell" data-posts-per-100>
            {postsPer100 != null ? formatRate(postsPer100) : <span className="text-muted-foreground">—</span>}
            {outcome?.posted != null && (
              <p className="text-xs text-muted-foreground">
                {outcome.posted.toLocaleString()} posted
                {outcome.joined != null ? ` · ${outcome.joined.toLocaleString()} joined` : ''}
              </p>
            )}
          </td>
        )}
        <td className="hidden py-2.5 pr-4 xl:table-cell">
          <RunSparkline runs={s.recentRuns} />
        </td>
        <td className="py-2.5 pr-1 text-right">
          <RowActions
            triggerLabel={`Actions for ${s.name}`}
            items={[
              { label: 'Run history', icon: History, onClick: onHistory },
              // Only when the server sends the stored search; an older one can't be edited from here.
              s.params && { label: 'Edit settings', icon: SlidersHorizontal, onClick: () => setEditing(true) },
              {
                label: s.enabled ? 'Pause' : 'Resume',
                icon: s.enabled ? Pause : Play,
                onClick: togglePause,
                disabled: update.isPending || (!s.enabled && archived),
              },
              { label: 'Delete', icon: Trash2, destructive: true, onClick: () => setDeleting(true) },
            ]}
          />
          {editing && <ScheduleEditDialog schedule={s} onClose={() => setEditing(false)} />}
          <ConfirmDialog
            open={deleting}
            onOpenChange={setDeleting}
            title="Delete this repeat run?"
            description={`"${s.name}" stops re-running and its run history is removed from this page. The ${s.totalAdded.toLocaleString()} creators it added stay in ${s.folder?.name ?? 'the folder'}.`}
            tone="destructive"
            confirmLabel="Delete schedule"
            pending={remove.isPending}
            onConfirm={() =>
              remove.mutateAsync(s.publicId).then(
                () => undefined,
                (e) => {
                  toast.error("Couldn't delete the schedule", { description: (e as Error).message });
                },
              )
            }
          />
        </td>
      </tr>
      {expanded && (
        <tr className="border-t border-border/60 bg-accent/15">
          <td colSpan={columns} className="px-4">
            <ScheduleCreators schedule={s} />
          </td>
        </tr>
      )}
    </Fragment>
  );
}
